import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Loader2, AlertTriangle, CheckCircle2, Cog, Award, RotateCcw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { formatMoney } from "../../lib/currency";

/**
 * MachineAlternativesDialog — drives the "Cambia macchina suggerita" UX.
 * Opens from a breakdown row in OperationsTab. Calls the backend ranker for
 * the specific operation and shows the top-N machines with score + WHY +
 * owned flag + trade-off Δ€/pz vs the current machine. "Applica" sends the
 * per-op machine override to /recalculate: the manual choice beats every
 * automatic rule (including Fantina Done-in-One) and costs are recomputed.
 */
export default function MachineAlternativesDialog({ open, onOpenChange, quoteId, operationType, operationLabel, opIndex, currentRow, currency = "EUR", fxRate = 1, onApply }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [applyingId, setApplyingId] = useState(null);
  const [applyError, setApplyError] = useState("");

  useEffect(() => {
    if (!open || !quoteId || !operationType) return;
    let cancelled = false;
    const load = async () => {
      setStatus("loading");
      setError("");
      setApplyError("");
      try {
        const { data } = await api.get(`/quotes/${quoteId}/operation-alternatives`, {
          params: { operation: operationType },
        });
        if (!cancelled) {
          setData(data);
          setStatus("ready");
        }
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err.response?.data?.detail) || err.message || "Errore");
          setStatus("error");
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, quoteId, operationType]);

  const close = () => {
    onOpenChange(false);
    setStatus("idle");
    setData(null);
    setApplyError("");
  };

  const apply = async (machineId) => {
    if (!onApply || opIndex == null) return;
    setApplyingId(machineId || "__reset__");
    setApplyError("");
    try {
      await onApply(opIndex, machineId);
      close();
    } catch (err) {
      setApplyError(formatApiError(err.response?.data?.detail) || err.message || t("machineAlts.errRecalc"));
    }
    setApplyingId(null);
  };

  // Stima trade-off: stessi minuti dell'op attuale sulla tariffa dell'alternativa.
  // Il ricalcolo reale applica poi calibrazione ed efficienza.
  const estDelta = (m) => {
    if (!currentRow || currentRow.minutes_per_piece == null || m.hourly_rate == null) return null;
    // €/h are EUR; the row cost is already in the quote currency.
    const est = (currentRow.minutes_per_piece / 60) * m.hourly_rate * fxRate;
    return est - (currentRow.cost_per_piece_eur || 0);
  };

  const currentMachineId = currentRow?.machine_id || currentRow?.external_machine_id || null;

  const scoreBadge = (s) => {
    if (s >= 80) return { cls: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300", label: t("machineAlts.scoreExcellent") };
    if (s >= 60) return { cls: "bg-blue-500/10 border-blue-500/40 text-blue-300", label: t("machineAlts.scoreGood") };
    if (s >= 40) return { cls: "bg-amber-500/10 border-amber-500/40 text-amber-300", label: t("machineAlts.scoreFair") };
    return { cls: "bg-rose-500/10 border-rose-500/40 text-rose-300", label: t("machineAlts.scoreLimited") };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-slate-900 border-slate-700" data-testid="machine-alternatives-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100 flex items-center gap-2">
            <Cog size={18} className="text-blue-400" />
            {t("quoteTabs.changeMachine")}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {t("machineAlts.descPrefix")} <span className="text-slate-200 font-semibold">{operationLabel || operationType}</span>.
            {" "}{t("machineAlts.descSuffix")}
          </DialogDescription>
        </DialogHeader>

        {status === "loading" && (
          <div className="py-10 flex flex-col items-center gap-3">
            <Loader2 size={28} className="animate-spin text-blue-400" />
            <div className="text-sm text-slate-400">{t("machineAlts.computing")}</div>
          </div>
        )}

        {status === "error" && (
          <div className="py-8 flex flex-col items-center gap-3" data-testid="alts-error">
            <AlertTriangle size={24} className="text-amber-400" />
            <div className="text-sm text-amber-300">{error}</div>
          </div>
        )}

        {status === "ready" && data && (
          <div className="space-y-3" data-testid="alts-result">
            <div className="px-3 py-2 bg-slate-800/50 border border-slate-700/40 rounded text-xs text-slate-400 font-mono-data flex items-center justify-between gap-3 flex-wrap">
              <span>{t("machineAlts.requiredTol")} <span className="text-slate-200">±{data.required_precision_um}µm</span></span>
              {currentRow && (
                <span>{t("machineAlts.current")} <span className="text-slate-200">{currentRow.machine_name}</span> · {formatMoney(currentRow.cost_per_piece_eur ?? 0, currency)}/pz</span>
              )}
            </div>
            {applyError && (
              <div className="px-3 py-2 rounded bg-red-500/10 border border-red-500/40 text-red-300 text-xs" data-testid="apply-error">
                {applyError}
              </div>
            )}
            <ul className="space-y-2">
              {data.alternatives.map((m, i) => {
                const badge = scoreBadge(m.score);
                const isCurrent = m.machine_id === currentMachineId;
                const delta = estDelta(m);
                return (
                  <li
                    key={`alt-${i}`}
                    className={`p-3 rounded-lg border ${isCurrent ? "border-blue-500/50 bg-blue-500/5" : m.owned ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-700 bg-slate-800/30"}`}
                    data-testid={`alt-${m.machine_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {m.owned && <CheckCircle2 size={14} className="text-emerald-400" title={t("machineAlts.machineOwned")}/>}
                          <span className="font-display font-semibold text-slate-100">{m.machine_name}</span>
                          {m.owned && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">{t("machineAlts.inWorkshop")}</span>}
                          {!m.owned && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">{t("machineAlts.external")}</span>}
                          {isCurrent && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">{t("machineAlts.currentBadge")}</span>}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{m.category}</div>
                        <div className="text-xs text-slate-300 mt-2 font-mono-data">{m.why}</div>
                        {delta != null && !isCurrent && (
                          <div className="text-[11px] mt-2 font-mono-data" data-testid={`alt-delta-${m.machine_id}`}>
                            {t("machineAlts.tradeOff")}{" "}
                            <span className={delta > 0.005 ? "text-rose-300" : "text-emerald-300"}>
                              {delta > 0 ? "+" : ""}{formatMoney(delta, currency)}/pz
                            </span>
                            {m.precision_um != null && currentRow?.hourly_rate != null && (
                              <span className="text-slate-500"> · {t("machineAlts.precisionSuffix", { v: m.precision_um })}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-2 justify-end">
                          <Award size={14} className={badge.cls.includes("emerald") ? "text-emerald-400" : badge.cls.includes("blue") ? "text-blue-400" : "text-amber-400"}/>
                          <span className="text-2xl font-display font-bold text-slate-100">{m.score}</span>
                          <span className="text-xs text-slate-500">/100</span>
                        </div>
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${badge.cls}`}>
                          {badge.label}
                        </span>
                        <div className="text-xs text-slate-400 mt-1 font-mono-data">€{m.hourly_rate}/h</div>
                        {!isCurrent && (
                          <button
                            type="button"
                            disabled={applyingId != null}
                            onClick={() => apply(m.machine_id)}
                            className="mt-2 px-3 py-1 rounded text-xs bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid={`apply-alt-${m.machine_id}`}
                          >
                            {applyingId === m.machine_id ? <Loader2 size={11} className="animate-spin"/> : null}
                            {t("machineAlts.apply")}
                          </button>
                        )}
                      </div>
                    </div>
                    {m.missing_ops?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-700/40 text-[11px] text-amber-300">
                        {t("machineAlts.notCovers", { ops: m.missing_ops.join(", ") })}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className="text-[11px] text-slate-500 text-center pt-2">
              {t("machineAlts.footer")}
            </p>
          </div>
        )}

        <div className="flex justify-between items-center pt-2 gap-3">
          {currentRow?.machine_overridden ? (
            <button
              type="button"
              disabled={applyingId != null}
              onClick={() => apply("")}
              className="px-3 py-1.5 text-sm rounded border border-slate-600 text-slate-300 hover:text-slate-100 hover:border-slate-400 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="alts-reset-btn"
            >
              {applyingId === "__reset__" ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
              {t("machineAlts.resetAuto")}
            </button>
          ) : <span/>}
          <button
            type="button"
            onClick={close}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-500 text-white"
            data-testid="alts-close-btn"
          >
            {t("common.close")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
